import React, { useEffect, useRef, useCallback } from "react";
import { Loader2, User } from "lucide-react";
import { RootState, useAppDispatch } from "../../../redux/store";
import { getNotificationDetail } from "../../../redux/features/notifications";
import { getNotifications } from "../../../redux/features/mobileNotifications";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { useNavigate } from "react-router";
import { setShowTopNav } from "../../../redux/features/mobileLayout";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout/slice";
import { decrementUnreadNotifCount } from "../../../redux/features/common/slice";
import { DateTimeUtil, getNotificationStatusLabel } from "../../../utils";
import { MobileNotification } from "../../../types";
import { setSelectedCategoryForDetails } from "../../../redux/features/notifications/slice";
import PullToRefresh from "pulltorefreshjs";
import { TimeoffNavTitle } from "../../../utils/timeoff";

const NotificationsMobile: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { isNotificationsLoading, notifications, notificationsMeta } = useSelector(
        (state: RootState) => state.mobileNotifications
    );
    const user = useSelector((state: RootState) => state.user.user);

    const scrollRef = useRef<HTMLDivElement | null>(null);

    const refreshData = useCallback(async () => {
        await dispatch(getNotifications({ page: 1 }));
    }, [dispatch]);

    useEffect(() => {
        dispatch(setShowTopNav(false));
        refreshData();

        PullToRefresh.init({
            mainElement: "#notif-refresh-container",
            onRefresh: () => refreshData(),
            instructionsPullToRefresh: "Pull to refresh",
            instructionsReleaseToRefresh: "Release to refresh",
            getMarkup: () => `
                <div class="w-full h-16 flex items-center justify-center bg-white shadow-sm">
                    <div class="w-full flex items-center justify-center">
                    <div class="flex items-center justify-center gap-2 text-sm text-gray-600">
                        <div class="h-4 w-4 border-2 border-t-transparent border-gray-500 rounded-full animate-spin"></div>
                        <span>Refreshing...</span>
                    </div>
                    </div>
                </div>
                `,
            distThreshold: 40,
            distMax: 60,
            distReload: 50,
            shouldPullToRefresh() {
                const container = document.getElementById("notif-refresh-container");
                return container?.scrollTop === 0;
            },
        });

        return () => {
            PullToRefresh.destroyAll();
        };
    }, [refreshData]);

    const loadMore = useCallback(() => {
        const nextPage = (notificationsMeta?.page ?? 1) + 1;
        const loadedCount = notifications.length;
        const totalCount = notificationsMeta?.count ?? 0;

        if (loadedCount >= totalCount || isNotificationsLoading) return;
        dispatch(getNotifications({ page: nextPage }));
    }, [dispatch, notifications.length, notificationsMeta?.count, notificationsMeta?.page, isNotificationsLoading]);

    const handleScroll = useCallback(() => {
        const container = scrollRef.current;
        if (!container || isNotificationsLoading) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        if (scrollTop + clientHeight >= scrollHeight - 100) {
            loadMore();
        }
    }, [loadMore, isNotificationsLoading]);

    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, [handleScroll]);

    const handleNotificationClick = (notification: MobileNotification) => {
        dispatch(setShowTopNav(true));
        dispatch(decrementUnreadNotifCount());
        dispatch(setSelectedCategoryForDetails(notification.notification_category));
        dispatch(
            getNotificationDetail({
                notificationId: notification.notification_id!,
                employeeId: user.id!,
            })
        );
        dispatch(
            setTopNavNameTitle(TimeoffNavTitle[notification.notification_category ?? ""] ?? "Notification Detail")
            // setTopNavNameTitle(notification.notification_display_name ?? "Notification Detail")
        );
        navigate("/m-notification-detail");
    };

    return (
        <div
            id="notif-refresh-container"
            ref={scrollRef}
            className="max-w-md mx-auto h-screen overflow-y-auto border rounded-lg shadow-md bg-white"
        >
            {isNotificationsLoading && notifications.length === 0 ? (
                <div className="flex justify-center items-center h-20">
                    <Loader2 className="w-8 h-8 text-gray-600 animate-spin" />
                </div>
            ) : notifications.length === 0 ? (
                <p className="text-gray-500 text-center p-4">No notifications found.</p>
            ) : (
                <ul className="divide-y divide-gray-200">
                    {notifications.map((notification, index) => {
                        const title = notification.message_info?.title || "Notification";
                        const body = notification.message_info?.body || "You have a new notification.";
                        const key = `${notification.notification_id}-${index}`;
                        const bgColor = notification.notification_read_status === "0" ? "bg-gray-100" : "bg-white";
                        const currentStatus = notification.workflow_current_step_details?.current_status;
                        const statusInfo = getNotificationStatusLabel(currentStatus);

                        return (
                            <li
                                key={key}
                                className={`flex items-start space-x-3 py-3 px-4 ${bgColor}`}
                                onClick={() => handleNotificationClick(notification)}
                            >
                                {notification.requestor?.image_url ? (
                                    <img
                                        src={notification.requestor.image_url}
                                        alt={notification.requestor.emp_name}
                                        className="w-10 h-10 rounded-full object-cover"
                                    />
                                ) : (
                                    <User className="w-10 h-10 text-gray-500" />
                                )}
                                <div className="flex-1">
                                    <h3 className="font-medium text-gray-900">{title}</h3>
                                    <p className="text-sm text-gray-700">{body}</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {DateTimeUtil.FormatWithDayjsUtc(
                                            notification.notification_date_time,
                                            DateTimeUtil.DATETIME_FANCY_2
                                        ) || "N/A"}{" "}
                                        |{" "}
                                        <span className={`${statusInfo.color}`}>
                                            {notification?.workflow_current_step_details?.status_text}
                                        </span>
                                    </p>
                                </div>
                            </li>
                        );
                    })}
                    {/* {isNotificationsLoading && (
                        <div className="flex justify-center items-center py-4">
                            <Loader2 className="w-6 h-6 animate-spin text-gray-600" />
                        </div>
                    )} */}
                </ul>
            )}
        </div>
    );
};

export default NotificationsMobile;
